/* eslint-disable no-restricted-imports */
import type { RuntimeHost } from '@/plc/runtime/RuntimeHost';
import { parseAddress, type ParsedAddress } from '@/plc/address/parser';
import type { ModbusMapping, ModbusDataArea } from './ModbusTypes';

/**
 * Handles mapping between Modbus logical addresses and PLC physical memory.
 */
export class ModbusMapper {
  private mappings: ModbusMapping[] = [];

  public setMappings(mappings: ModbusMapping[]) {
    this.mappings = mappings;
  }

  public getMappings(): ModbusMapping[] {
    return this.mappings;
  }

  public read(host: RuntimeHost, area: ModbusDataArea, address: number): number | null {
    const mapping = this.mappings.find(m => m.area === area && m.modbusAddress === address);
    if (!mapping) return null;

    const parsed = parseAddress(mapping.plcAddress);
    if (!parsed) return null;

    return this.readPlcMemory(host, parsed, area);
  }

  public write(host: RuntimeHost, area: ModbusDataArea, address: number, value: number): boolean {
    // Only COIL and HOLDING_REGISTER are writable from Modbus client
    if (area === 'DISCRETE_INPUT' || area === 'INPUT_REGISTER') return false;

    const mapping = this.mappings.find(m => m.area === area && m.modbusAddress === address);
    if (!mapping) return false;

    const parsed = parseAddress(mapping.plcAddress);
    if (!parsed) return false;

    return this.writePlcMemory(host, parsed, area, value);
  }

  private readPlcMemory(host: RuntimeHost, parsed: ParsedAddress, area: ModbusDataArea): number {
    const { area: plcArea, bitOffset, byteOffset, size } = parsed;
    let memoryArea;

    if (plcArea === 'I') memoryArea = host.pi.inputs;
    else if (plcArea === 'Q') memoryArea = host.pi.outputs;
    else if (plcArea === 'M') memoryArea = host.pi.memory;
    else return 0;

    if (area === 'COIL' || area === 'DISCRETE_INPUT') {
      if (size !== 'X') return 0; // Must be bit addressing
      return memoryArea.getBit(byteOffset, bitOffset) ? 1 : 0;
    } else {
      if (size !== 'W') return 0; // Modbus registers are words (16-bit)
      // Read unsigned word
      return memoryArea.getWord(byteOffset);
    }
  }

  private writePlcMemory(host: RuntimeHost, parsed: ParsedAddress, area: ModbusDataArea, value: number): boolean {
    const { area: plcArea, bitOffset, byteOffset, size } = parsed;
    let memoryArea;

    // Typically, Modbus client writes to M or Q. Writing to I is usually not allowed,
    // but in a simulator, we might allow it depending on rules. Let's allow M and Q.
    if (plcArea === 'I') return false; 
    else if (plcArea === 'Q') memoryArea = host.pi.outputs;
    else if (plcArea === 'M') memoryArea = host.pi.memory;
    else return false;

    if (area === 'COIL') {
      if (size !== 'X') return false;
      memoryArea.setBit(byteOffset, bitOffset, value !== 0);
      return true;
    } else if (area === 'HOLDING_REGISTER') {
      if (size !== 'W') return false;
      memoryArea.setWord(byteOffset, value);
      return true;
    }
    return false;
  }
}
